/**
 * reportPeriod — os PRESETS de período e o RÓTULO deles, num lugar só.
 *
 * Existiam dois filtros de período que não se falavam: o `DateRangeFilter` de
 * /relatorios (7 dias … 12 meses, sem nenhum preset curto) e o `PeriodFilter` do
 * Dashboard (Hoje / Esta semana / Este mês). O mesmo texto podia significar
 * coisas diferentes em cada tela, e o relatório não tinha como responder "quanto
 * entrou hoje?".
 *
 * Aqui ficam a MATEMÁTICA da faixa e o RÓTULO. Os dois componentes continuam
 * existindo (ver "Por que não um componente só" abaixo), mas a definição de
 * "Esta semana" passa a ser uma só.
 *
 * ─── A SEMANA COMEÇA NO DOMINGO ─────────────────────────────────────────────
 *
 * `weekStartsOn: 0`, igual à Agenda (`AgendaPage`) e ao Dashboard. Se um dia o
 * app mudar para segunda, muda aqui e as três telas acompanham.
 *
 * ─── DIA INTEIRO NAS PONTAS ─────────────────────────────────────────────────
 *
 * `created_at` é timestamp. Um preset "Hoje" que devolvesse `now → now` não
 * pegaria nada, e "Ontem" pegaria meio dia. Todo preset curto é ancorado em
 * `startOfDay`/`endOfDay`. Os presets longos herdados (`subDays(now, 7)`)
 * mantêm o comportamento histórico de propósito — mudá-los alteraria números de
 * relatórios que o usuário já conhece.
 *
 * ─── O RÓTULO: PRESET PRIMEIRO, CONTAGEM DE DIAS SÓ COMO ÚLTIMO RECURSO ─────
 *
 * O rótulo do total ("Total nos últimos 6 meses") era DEDUZIDO da quantidade de
 * dias, e por isso errava sempre (armadilha #45): `subMonths(hoje, 6)` dá
 * 181–184 dias, e o limite estava escrito com o número NOMINAL do mês (180) —
 * então TODO preset caía no rótulo do preset seguinte, e a tela escrevia "nos
 * últimos 12 meses" com "Últimos 6 meses" selecionado no filtro.
 *
 * Duas correções, as duas necessárias:
 *  1. quando o preset é conhecido, o rótulo vem DELE — dedução nenhuma (é o
 *     único jeito de distinguir "Hoje" de "Ontem", ou "Esta semana" de
 *     "Últimos 7 dias": as faixas têm o mesmo tamanho);
 *  2. o classificador por dias, que sobra para a faixa personalizada e para
 *     chamadores sem preset, usa os TETOS REAIS (7 / 31 / 92 / 184 / 366),
 *     nunca a média de 30 dias por mês.
 *
 * ─── POR QUE NÃO UM COMPONENTE SÓ ───────────────────────────────────────────
 *
 * Avaliado e recusado: o filtro de /relatorios é um `Select` de 12 opções com
 * calendário duplo para a faixa personalizada; o do Dashboard é um botão-fantasma
 * de 3 opções dentro de um cabeçalho denso. Um componente único ou daria ao
 * Dashboard um seletor gigante (o enunciado pede explicitamente para não) ou
 * exigiria uma prop de subconjunto que reintroduz duas listas de rótulos — que é
 * o problema original. O que precisava ser único era a SEMÂNTICA, e é isso que
 * este módulo garante: `resolvePeriodPreset('thisWeek')` é a mesma semana nas
 * duas telas.
 */
import {
  endOfDay,
  endOfMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';

/** Faixa de datas usada pelos relatórios (mesma forma do `DateRangeFilter`). */
export interface ReportDateRange {
  from: Date;
  to: Date;
}

export type ReportPeriodPreset =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | '7d'
  | '30d'
  | 'thisMonth'
  | '3m'
  | '6m'
  | '12m'
  | 'thisYear'
  | 'custom';

export type WeekStartsOn = 0 | 1;
/** Compatibilidade: empresas existentes continuam começando no domingo. */
export const DEFAULT_WEEK_STARTS_ON: WeekStartsOn = 0;
/** @deprecated Use a preferência da empresa; mantido para consumidores antigos. */
export const WEEK_STARTS_ON = DEFAULT_WEEK_STARTS_ON;

export function normalizeWeekStartsOn(value: unknown): WeekStartsOn {
  return value === 1 ? 1 : DEFAULT_WEEK_STARTS_ON;
}

export function companyWeekStartsOn(
  company: { week_starts_on?: number | null } | null | undefined,
): WeekStartsOn {
  return normalizeWeekStartsOn(company?.week_starts_on);
}

/**
 * Rótulo do seletor. A ordem é do mais curto para o mais longo — quem procura
 * "Hoje" não deveria ter que rolar até o fim de uma lista que começa em 7 dias.
 */
export const PERIOD_PRESET_LABELS: Record<ReportPeriodPreset, string> = {
  today: 'Hoje',
  yesterday: 'Ontem',
  thisWeek: 'Esta semana',
  lastWeek: 'Semana passada',
  '7d': 'Últimos 7 dias',
  '30d': 'Últimos 30 dias',
  thisMonth: 'Este mês',
  '3m': 'Últimos 3 meses',
  '6m': 'Últimos 6 meses',
  '12m': 'Últimos 12 meses',
  thisYear: 'Este ano',
  custom: 'Personalizado',
};

/** Ordem de exibição no seletor de /relatorios. */
export const PERIOD_PRESET_ORDER: ReportPeriodPreset[] = [
  'today',
  'yesterday',
  'thisWeek',
  'lastWeek',
  '7d',
  '30d',
  'thisMonth',
  '3m',
  '6m',
  '12m',
  'thisYear',
  'custom',
];

/**
 * O que o Dashboard oferece hoje. Fica aqui para que, se o `PeriodFilter` um dia
 * passar a consumir este módulo, o subconjunto seja explícito em vez de uma
 * segunda lista de rótulos copiada.
 */
export const DASHBOARD_PRESETS: ReportPeriodPreset[] = ['today', 'thisWeek', 'thisMonth'];

/**
 * Frase que completa "Total …" nos totais dos relatórios. Vem do PRESET, não de
 * uma dedução — "Hoje" e "Ontem" têm exatamente o mesmo tamanho de faixa.
 */
const PERIOD_TOTAL_PHRASES: Record<Exclude<ReportPeriodPreset, 'custom'>, string> = {
  today: 'hoje',
  yesterday: 'ontem',
  thisWeek: 'nesta semana',
  lastWeek: 'na semana passada',
  '7d': 'nos últimos 7 dias',
  '30d': 'nos últimos 30 dias',
  thisMonth: 'neste mês',
  '3m': 'nos últimos 3 meses',
  '6m': 'nos últimos 6 meses',
  '12m': 'nos últimos 12 meses',
  thisYear: 'neste ano',
};

export const CUSTOM_PERIOD_PHRASE = 'no período selecionado';

/**
 * Faixa de um preset. `custom` devolve `null` — quem escolhe a faixa é o
 * usuário, no calendário.
 *
 * `now` é injetável para os testes fixarem o "hoje" sem mexer em relógio global.
 */
export function resolvePeriodPreset(
  preset: ReportPeriodPreset,
  now: Date = new Date(),
  weekStartsOn: WeekStartsOn = DEFAULT_WEEK_STARTS_ON,
): ReportDateRange | null {
  switch (preset) {
    // ===== presets curtos: dia INTEIRO nas duas pontas =====
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now) };
    case 'yesterday': {
      const day = subDays(now, 1);
      return { from: startOfDay(day), to: endOfDay(day) };
    }
    case 'thisWeek':
      return { from: startOfWeek(now, { weekStartsOn }), to: endOfDay(now) };
    case 'lastWeek': {
      const start = startOfWeek(subWeeks(now, 1), { weekStartsOn });
      // Fim = sábado daquela semana (6 dias depois do domingo), dia inteiro.
      return { from: start, to: endOfDay(subDays(startOfWeek(now, { weekStartsOn }), 1)) };
    }

    // ===== presets herdados: comportamento histórico preservado =====
    case '7d':
      return { from: subDays(now, 7), to: now };
    case '30d':
      return { from: subDays(now, 30), to: now };
    case 'thisMonth':
      // Vai até o FIM do mês (não até hoje) de propósito: na base "vencimento"
      // do Relatório de Pagamentos é justamente o que ainda vai vencer que
      // interessa. Para `created_at` dá no mesmo — não há linha no futuro.
      return { from: startOfMonth(now), to: endOfMonth(now) };
    case '3m':
      return { from: subMonths(now, 3), to: now };
    case '6m':
      return { from: subMonths(now, 6), to: now };
    case '12m':
      return { from: subMonths(now, 12), to: now };
    case 'thisYear':
      return { from: startOfYear(now), to: now };
    case 'custom':
      return null;
  }
}

/** Dias inteiros cobertos pela faixa (arredondando para cima). */
export function periodDays(range: ReportDateRange): number {
  const ms = range.to.getTime() - range.from.getTime();
  if (!Number.isFinite(ms) || ms < 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/**
 * Classificação por CONTAGEM DE DIAS — o caminho de último recurso, para faixa
 * personalizada ou chamador sem preset.
 *
 * ⚠️ Os limites são os MÁXIMOS REAIS de cada preset, nunca os nominais
 * (armadilha #45). Um mês tem 28–31 dias:
 *   - `subMonths(hoje, 3)`  → 89–92 dias   (não 90)
 *   - `subMonths(hoje, 6)`  → 181–184 dias (não 180)
 *   - `subMonths(hoje, 12)` → 365–366 dias
 * Com 180 no lugar de 184, "Últimos 6 meses" era classificado como 12 meses e a
 * tela escrevia o rótulo errado — sem erro nenhum, só um texto mentiroso.
 */
export function periodLabelFromDays(diffDays: number): string {
  if (diffDays <= 7) return PERIOD_TOTAL_PHRASES['7d'];
  if (diffDays <= 31) return PERIOD_TOTAL_PHRASES['30d'];
  if (diffDays <= 92) return PERIOD_TOTAL_PHRASES['3m'];
  if (diffDays <= 184) return PERIOD_TOTAL_PHRASES['6m'];
  if (diffDays <= 366) return PERIOD_TOTAL_PHRASES['12m'];
  return CUSTOM_PERIOD_PHRASE;
}

/**
 * A frase do total. Com preset conhecido (e diferente de `custom`) sai dele;
 * `custom` sai como "no período selecionado" — inventar "nos últimos 30 dias"
 * para uma faixa escolhida a dedo seria mentira. Sem preset, cai no
 * classificador por dias.
 */
export function periodLabel(
  range: ReportDateRange,
  preset?: ReportPeriodPreset | null,
): string {
  if (preset === 'custom') return CUSTOM_PERIOD_PHRASE;
  if (preset) return PERIOD_TOTAL_PHRASES[preset];
  return periodLabelFromDays(periodDays(range));
}
